/**
 * Client-side reporting — the console's half of the dogfood observability
 * loop. Reports land as `client_event` facts via
 * `POST /observability/client-events` (unauthenticated by design — a
 * login-page crash must still report, and the public acquisition surfaces
 * have no session to report with; the backend bounds abuse with a per-IP
 * rate limit + body cap + per-field length caps).
 *
 * Sources: window 'error' + 'unhandledrejection' (installed once from
 * main.tsx), ErrorBoundary.componentDidCatch, the transport layer
 * (apiFetch/gatewayFetch report network failures + 5xx responses only —
 * 4xx/503-cold-start are handled flow, not defects), and the two anonymous
 * non-error kinds: the marketing funnel (`reportFunnelStep`) and the console
 * route-switch page view (`reportPageView`).
 *
 * Rules:
 * - Never throws, never blocks — every failure is swallowed; reporting
 *   must never crash or slow the app it observes.
 * - Content discipline: message/stack are error text only, capped here AND
 *   at ingest; `path` is the SPA pathname only (never query — orgId and
 *   worse can ride it); no request bodies, response bodies, cookies,
 *   headers, or storage values are ever sent. Wire errors
 *   (ApiError/GatewayApiError) are message/stack-suppressed entirely — their
 *   message IS response text (envelope message or raw body) and their stack
 *   repeats it — they beacon the fixed `name: code (HTTP status)` label only.
 * - Client-side throttle + dedup: a crash loop cannot beacon-storm the
 *   endpoint — MAX_PER_SESSION total, MAX_PER_WINDOW per WINDOW_MS, and the
 *   same (kind + message + path) dedups inside a window.
 * - sendBeacon is the primary transport (it survives page unload — the
 *   exact moment crash reports matter most); fetch+keepalive is the
 *   fallback when sendBeacon is unavailable or refuses the payload.
 * - VITE_MOCK mode: the whole module is a no-op — a mock backend has no
 *   /observability endpoint and demo sessions produce no real signal.
 */
import { ACTIVE_ORG_KEY } from '@/lib/constants'
import type { paths } from '@/types/openapi'

// Same VITE_API_BASE env read as api.ts (this module cannot import it —
// api.ts imports reportError). The transport appends '/api'; the beacon is
// a raw fetch/sendBeacon so it joins the namespace itself.
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''
const ENDPOINT = `${API_BASE}/api/observability/client-events`
// Read lazily (not a module-level const) so tests can pin VITE_MOCK via
// vi.stubEnv — a static import would capture the ambient local .env instead.
function mockEnabled(): boolean {
  return ['true', '1', 'on', 'yes'].includes(
    String(import.meta.env.VITE_MOCK ?? '').toLowerCase(),
  )
}

const WINDOW_MS = 60_000
const MAX_PER_WINDOW = 20
const MAX_PER_SESSION = 50
const MAX_MESSAGE = 2000
const MAX_STACK = 8000

// The funnel kind gets its own budget — see reportFunnelStep. Bounded, because
// the endpoint is unauthenticated: the backend's per-IP limiter is the real
// ceiling, this one just keeps a stuck effect from looping the beacon.
const FUNNEL_MAX_PER_SESSION = 40
const FUNNEL_DEDUPE_MS = 2_000

// The page-view kind gets a third budget of its own — see reportPageView.
// A route switch is ordinary traffic, so it must neither starve an error
// report nor be starved by one; the ceiling here is far above a human
// session's worth of navigations, and only exists so a stuck effect (or a
// redirect loop) cannot beacon-storm the unauthenticated endpoint.
const PAGE_MAX_PER_SESSION = 200
const PAGE_DEDUPE_MS = 2_000

export type ClientEventKind =
  | 'window_error'
  | 'unhandled_rejection'
  | 'error_boundary'
  | 'api_error'
  | 'funnel_step'
  // Wire has it (gh #248); the console emits it from the route-switch
  // beacon (see reportPageView). page_view requires `path`, and the backend
  // strips the query and the identity fields from it server-side.
  | 'page_view'

/**
 * The closed funnel label set — hand-kept mirror of `FUNNEL_STEPS` in the
 * backend's `modules/analytics/service.ts`, which the beacon route enforces
 * (an `extra.step` off that list is a 400 before the fact queues). Adding a
 * step therefore means both repos; a label shipped only here would be
 * silently refused, and one added only there is unreachable.
 */
export const FUNNEL_STEPS = [
  'calculator_view',
  'calculator_click_pricing',
  'calculator_click_uses',
] as const

export type FunnelStep = (typeof FUNNEL_STEPS)[number]

// Both vocabularies are hand-kept here (the generated contract is types-only,
// and this module is imported *by* api.ts so it cannot import it back), so pin
// them to the wire: tsc fails the moment the backend's enum moves.
type WireEventKind =
  paths['/api/observability/client-events']['post']['requestBody']['content']['application/json']['eventKind']
type AssertEqual<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never
const EVENT_KIND_PARITY: AssertEqual<ClientEventKind, WireEventKind> = true
void EVENT_KIND_PARITY

export interface ClientEventInput {
  eventKind: ClientEventKind
  message?: string
  stack?: string
  /** `x-request-id` / `x-trace-id` off the failing response — join keys
   *  into analytics_inference_requests on the backend. */
  requestId?: string | null
  traceId?: string | null
  httpStatus?: number
  /** Small structured fields (error name, component stack, endpoint) —
   *  never user input. */
  extra?: Record<string, string | number | boolean>
}

let windowStart = 0
let sentThisWindow = 0
let sentThisSession = 0
/** dedupKey → last-sent epoch ms (inside the current window). */
const seen = new Map<string, number>()
let funnelSentThisSession = 0
/** step → last-sent epoch ms (the funnel's own budget, never the error one). */
const funnelSeenAt = new Map<string, number>()
let pageSentThisSession = 0
/** route path → last-sent epoch ms (the page kind's own dedupe, never the error one). */
const pageSeenAt = new Map<string, number>()

function mintEventId(): string {
  // crypto.randomUUID exists on all modern browsers + jsdom; the fallback
  // keeps very old engines reporting (the backend only needs ≤64 chars).
  return typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID()
    : `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

function storedOrgId(): string | undefined {
  try {
    const v = localStorage.getItem(ACTIVE_ORG_KEY)
    // The backend enforces uuid shape — drop a non-uuid value client-side
    // rather than send a guaranteed-400.
    return v !== null &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)
      ? v
      : undefined
  } catch {
    return undefined
  }
}

function send(payload: Record<string, unknown>): void {
  try {
    const init = {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'omit' as const,
      keepalive: true,
      body: JSON.stringify(payload),
    }
    // Blob carries the JSON content-type; sendBeacon queues delivery even
    // mid-unload where a keepalive fetch can still be cancelled.
    if (typeof navigator.sendBeacon === 'function') {
      if (navigator.sendBeacon(ENDPOINT, new Blob([init.body], { type: 'application/json' }))) return
    }
    void fetch(ENDPOINT, init).catch(() => {})
  } catch {
    // reporting must never throw
  }
}

/** The fields every report carries — so the slicing caps and the build
 *  identity live once. `orgId` is NOT here on purpose: error reports correlate
 *  by org, funnel steps stay anonymous (see {@link reportFunnelStep}). */
function reportBase(now: number) {
  return {
    eventId: mintEventId(),
    occurredAtMs: now,
    path: location.pathname.slice(0, 500),
    userAgent: navigator.userAgent.slice(0, 300),
    // Build identity (vite define → SOURCE_COMMIT at image build) — a report
    // that can't be pinned to a deploy can't be read as one. Wire name is
    // `appVersion` (analytics_client_events.app_version); the ingest schema has
    // no `buildId`, so sending that key drops the value in silence.
    appVersion: typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev',
  }
}

export function reportClientEvent(input: ClientEventInput): void {
  if (mockEnabled()) return
  try {
    const now = Date.now()
    if (now - windowStart > WINDOW_MS) {
      windowStart = now
      sentThisWindow = 0
      seen.clear()
    }
    if (sentThisSession >= MAX_PER_SESSION || sentThisWindow >= MAX_PER_WINDOW) return
    const dedupKey = `${input.eventKind}:${input.message ?? ''}:${location.pathname}`
    const seenAt = seen.get(dedupKey)
    if (seenAt !== undefined && now - seenAt < WINDOW_MS) return
    seen.set(dedupKey, now)
    sentThisWindow++
    sentThisSession++

    send({
      ...reportBase(now),
      orgId: storedOrgId(),
      eventKind: input.eventKind,
      message: input.message?.slice(0, MAX_MESSAGE),
      stack: input.stack?.slice(0, MAX_STACK),
      requestId: input.requestId ?? undefined,
      traceId: input.traceId ?? undefined,
      httpStatus: input.httpStatus,
      extra: input.extra,
    })
  } catch {
    // reporting must never throw
  }
}

/**
 * The anonymous marketing step — the only non-error kind, and the reason the
 * public surfaces can count anything at all (a landing→calculator click is a
 * client-side navigation: it produces no HTTP request, so no access log sees
 * it). Write-only: it reads no server state and feeds nothing displayed.
 *
 * No `orgId`: a marketing funnel measures anonymous visitors, and bolting a
 * self-reported tenant onto it buys nothing while widening what the public
 * beacon can carry.
 *
 * Own budget, deliberately. The error caps are tuned for crash loops; a page
 * view is ordinary traffic and must neither starve an error report nor be
 * starved by one. The per-step collapse window exists for exactly one thing —
 * StrictMode double-invokes the mount effect in dev, and a dev artifact must
 * not land as two views. A real re-entry (back out, come back) is seconds to
 * minutes later and still counts.
 */
export function reportFunnelStep(step: FunnelStep): void {
  if (mockEnabled()) return
  try {
    const now = Date.now()
    if (funnelSentThisSession >= FUNNEL_MAX_PER_SESSION) return
    const last = funnelSeenAt.get(step)
    if (last !== undefined && now - last < FUNNEL_DEDUPE_MS) return
    funnelSeenAt.set(step, now)
    funnelSentThisSession++
    send({
      ...reportBase(now),
      eventKind: 'funnel_step',
      extra: { step },
    })
  } catch {
    // reporting must never throw
  }
}

/**
 * The console route switch — one anonymous page view per console route.
 *
 * A client-side navigation produces no HTTP request, so no access log sees
 * it: without this, the backend knows how many errors the console raised but
 * not how many pages it showed. The path is the route-level pathname (query
 * stripped here AND server-side), so `/deployments?org=…` is one view of
 * `/deployments`, not one view per query string.
 *
 * Anonymous like the funnel: no `orgId`, no user id, no message/stack — a
 * page view is not a defect and must not be shaped like one. The backend
 * refuses a `page_view` without `path`, so a blank/relative path is dropped
 * client-side rather than sent as a guaranteed 400.
 *
 * Own budget and own dedupe map (never the error ones): ordinary traffic
 * must neither starve an error report nor be starved by one. The per-path
 * collapse window exists for the same reason as the funnel's — StrictMode
 * double-invokes the mount effect in dev, and a dev artifact must not land
 * as two views. A real re-entry (back out, come back) is seconds to minutes
 * later and still counts.
 */
export function reportPageView(path: string): void {
  if (mockEnabled()) return
  try {
    // Route level only: the query can carry an orgId and worse. The backend
    // strips it too — this is the belt to that suspenders.
    const route = path.split('?')[0]?.slice(0, 500) ?? ''
    // The backend refuses a page_view without a path — don't spend the call.
    if (!route || !route.startsWith('/')) return
    const now = Date.now()
    if (pageSentThisSession >= PAGE_MAX_PER_SESSION) return
    const last = pageSeenAt.get(route)
    if (last !== undefined && now - last < PAGE_DEDUPE_MS) return
    pageSeenAt.set(route, now)
    pageSentThisSession++
    send({
      ...reportBase(now),
      // Route-level path, overriding reportBase's full location.pathname.
      path: route,
      eventKind: 'page_view',
    })
  } catch {
    // reporting must never throw
  }
}

/**
 * Normalize an unknown thrown value into a client event. Correlation ids
 * are picked up duck-typed — ApiError/GatewayApiError carry `requestId`,
 * `traceId`, `status` captured off the failing response.
 *
 * Wire errors (duck-typed `code: string` + `status: number` — the shape both
 * transport classes carry by construction; this module cannot import api.ts,
 * which imports it) never contribute text: their `message` is server response
 * text (envelope message, or the raw body for unrecognized payloads) and V8
 * `stack` repeats that message on its first line. They beacon the fixed
 * `name: code (HTTP status)` label instead — code + status + correlation ids,
 * zero response text, on every reporting path. Display is untouched: the
 * thrown error keeps its message; only this reporter substitutes. Non-wire
 * errors (client-side exceptions) keep message + stack.
 */
export function reportError(
  err: unknown,
  eventKind: ClientEventKind,
  fields: { extra?: ClientEventInput['extra'] } = {},
): void {
  const e = err instanceof Error ? err : new Error(typeof err === 'string' ? err : 'non-error thrown')
  const corr = err as { requestId?: string; traceId?: string; code?: unknown; status?: unknown }
  const wireCode = typeof corr.code === 'string' ? corr.code : null
  const wireStatus = typeof corr.status === 'number' ? corr.status : null
  const wireLabel =
    wireCode !== null && wireStatus !== null ? `${e.name}: ${wireCode} (HTTP ${wireStatus})` : null
  reportClientEvent({
    eventKind,
    message: (wireLabel ?? `${e.name}: ${e.message}`).slice(0, MAX_MESSAGE),
    stack: wireLabel === null ? e.stack : undefined,
    requestId: corr.requestId,
    traceId: corr.traceId,
    httpStatus: wireStatus ?? undefined,
    extra: fields.extra,
  })
}

let installed = false
/**
 * Mount the global capture surfaces once (main.tsx). Idempotent — repeat
 * calls (HMR, tests) don't double-report.
 */
export function installGlobalErrorReporters(): void {
  if (installed || mockEnabled()) return
  installed = true
  window.addEventListener('error', (ev) => {
    reportError(ev.error ?? ev.message, 'window_error', {
      extra: {
        filename: String(ev.filename ?? ''),
        lineno: ev.lineno ?? 0,
        colno: ev.colno ?? 0,
      },
    })
  })
  window.addEventListener('unhandledrejection', (ev) => {
    reportError(ev.reason, 'unhandled_rejection')
  })
}

/** Test seam — reset the throttle/dedup window between specs. */
export function resetClientEventThrottleForTest(): void {
  windowStart = 0
  sentThisWindow = 0
  sentThisSession = 0
  seen.clear()
  funnelSentThisSession = 0
  funnelSeenAt.clear()
  pageSentThisSession = 0
  pageSeenAt.clear()
}
